import {
  UserRole,
  type DataFileListItem,
  type DriverListItem,
  type KartListItem,
  type User,
} from '@kangaroo/shared';

import { findDriverByName, type TelemetrySessionOption } from './telemetrySessions';

export type TelemetrySessionFilterScope = 'all-team' | 'driver-scoped';

export function canSelectAnyTeamDriver(role: UserRole) {
  return role === UserRole.TEAM_OWNER || role === UserRole.COACH || role === UserRole.MECHANIC;
}

export function isSelfScopedDriverRole(role: UserRole) {
  return role === UserRole.DRIVER;
}

export function isParentScopedDriverRole(role: UserRole) {
  return role === UserRole.PARENT;
}

export function filterDriversForUser(allDrivers: DriverListItem[], user: User | null): DriverListItem[] {
  if (!user) {
    return [];
  }

  if (canSelectAnyTeamDriver(user.role)) {
    return allDrivers;
  }

  if (isSelfScopedDriverRole(user.role)) {
    return allDrivers.filter((driver) => driver.userId === user.id);
  }

  if (isParentScopedDriverRole(user.role)) {
    return allDrivers.filter((driver) => driver.parents.some((parent) => parent.userId === user.id));
  }

  return [];
}

export function isDriverSelectionLocked(user: User | null) {
  return Boolean(user && isSelfScopedDriverRole(user.role));
}

export function canClearDriverSelection(user: User | null) {
  return Boolean(user && canSelectAnyTeamDriver(user.role));
}

function normalizeKartNumber(value: string) {
  return value.replace(/^#/, '').trim().toLowerCase();
}

export function dataFileMatchesDriver(file: DataFileListItem, driver: DriverListItem) {
  if (file.sessionAssignment) {
    return file.sessionAssignment.driverId === driver.id;
  }

  if (file.uploadedByUserId === driver.userId) {
    return true;
  }

  return Boolean(findDriverByName([driver], file.summary.driverName ?? null));
}

export function shouldAutoAssignLockedDriver(
  driverSelectionLocked: boolean,
  driverAssignmentSet: boolean | undefined,
  boundDriverId: string | null,
  scopedDefaultDriverId: string | null,
) {
  return Boolean(
    driverSelectionLocked
    && driverAssignmentSet !== true
    && scopedDefaultDriverId
    && boundDriverId !== scopedDefaultDriverId
  );
}

export function filterKartsForDriver(karts: KartListItem[], driver: DriverListItem | null) {
  if (!driver) {
    return karts;
  }

  const assigned = karts.filter((kart) => kart.assignedDriverId === driver.id);
  return assigned.length > 0 ? assigned : karts;
}

export function filterTelemetrySessionOptions(
  options: TelemetrySessionOption[],
  driver: DriverListItem | null,
  kart: KartListItem | null,
  scope: TelemetrySessionFilterScope,
) {
  if (scope === 'all-team') {
    return options.filter((option) => {
      if (driver && (!option.dataFile || !dataFileMatchesDriver(option.dataFile, driver))) {
        return false;
      }

      if (!kart || !option.dataFile?.summary.kartNumber) {
        return true;
      }

      return normalizeKartNumber(option.dataFile.summary.kartNumber) === normalizeKartNumber(kart.kartNumber);
    });
  }

  if (!driver) {
    return [];
  }

  return options.filter((option) => {
    if (!option.dataFile) {
      return false;
    }

    if (!dataFileMatchesDriver(option.dataFile, driver)) {
      return false;
    }

    if (kart && option.dataFile.summary.kartNumber) {
      return normalizeKartNumber(option.dataFile.summary.kartNumber) === normalizeKartNumber(kart.kartNumber);
    }

    return true;
  });
}
